
import React from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useUserNotificationSettings } from '@/hooks/profile/useUserNotificationSettings';
import { useAuth } from '@/contexts/auth';

export const NotificationPreferences: React.FC = () => {
  const { user } = useAuth();
  const { settings, loading, saving, updateSetting, saveSettings } = useUserNotificationSettings();

  if (!user) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notification Preferences</CardTitle>
          <CardDescription>
            You need to be signed in to manage your notification preferences.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notification Preferences</CardTitle>
          <CardDescription>
            Loading your notification settings...
          </CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-nation-purple" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notification Preferences</CardTitle>
        <CardDescription>
          Customize how and when you receive notifications
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {settings.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              No notification settings available.
            </div>
          ) : (
            settings.map((setting, index) => (
              <div key={setting.notification_type} className="border-b pb-4 last:border-0">
                <div className="flex flex-col space-y-1.5 mb-3">
                  <h3 className="text-sm font-medium">{setting.label}</h3>
                  <p className="text-xs text-muted-foreground">{setting.description}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm">Email notifications</div>
                    <Switch 
                      checked={setting.email_enabled} 
                      onCheckedChange={(checked) => updateSetting(index, 'email_enabled', checked)} 
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-sm">In-app notifications</div>
                    <Switch 
                      checked={setting.in_app_enabled} 
                      onCheckedChange={(checked) => updateSetting(index, 'in_app_enabled', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-sm">Frequency</div>
                    <Select 
                      value={setting.frequency} 
                      onValueChange={(value: 'immediate' | 'daily' | 'never') => 
                        updateSetting(index, 'frequency', value)
                      }
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="immediate">Immediate</SelectItem>
                        <SelectItem value="daily">Daily digest</SelectItem>
                        <SelectItem value="never">Never</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button 
          onClick={saveSettings} 
          disabled={saving}
          variant="purple"
        >
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save Preferences
        </Button>
      </CardFooter>
    </Card>
  );
};
